/**
 * AskSession Utility Functions
 *
 * This file contains utility functions used by the AskSession component.
 * These functions handle message transformation, platform detection, and
 * other reusable logic to keep the main component file clean and maintainable.
 */

import { AskSessionMessage } from '@/apis/ask/askHistory/types';
import { AskMessage } from '@/components/common/Ask/Ask';

// ============= Utilities =============

/**
 * Transforms a single API message to a component message format.
 * Handles role detection and user identification.
 *
 * @param msg - The API message to transform
 * @param currentUserId - The current user's ID for comparison
 * @returns Transformed AskMessage object
 */
export const transformApiMessageToComponent = (
  msg: AskSessionMessage,
  currentUserId: string
): AskMessage => {
  // API responses are not fully consistent:
  // - `role` is *typed* as string, but SignalR payloads can contain numeric roles.
  // - markdown rendering depends on `senderId === 'assistant'` (see `ChatMessages.tsx`).
  // So we normalise using both role and userId/userName fallbacks.
  const roleRaw = msg.role ?? msg.authorRole ?? '';
  const roleNormalized =
    typeof roleRaw === 'string' ? roleRaw.trim().toLowerCase() : '';

  const userIdNormalized =
    typeof msg.userId === 'string' ? msg.userId.trim().toLowerCase() : '';
  const userNameNormalized =
    typeof msg.userName === 'string' ? msg.userName.trim().toLowerCase() : '';

  const isCurrentUserMessage = msg.userId === currentUserId;

  const isAssistantMessage =
    // Primary: role indicates assistant/system.
    roleNormalized === 'assistant' ||
    roleNormalized === 'system' ||
    // Fallback: SignalR payloads sometimes provide `userId: "Assistant"` even when `role` is unexpected.
    userIdNormalized === 'assistant' ||
    userIdNormalized === 'system' ||
    userNameNormalized === 'assistant' ||
    userNameNormalized === 'system';

  const citations = Array.isArray(msg.citations)
    ? (msg.citations as import('@/apis/ask/askHistory/types').AskSessionCitation[])
    : null;
  const citationsUsed = Array.isArray(msg.citationsUsed)
    ? (msg.citationsUsed as string[])
    : null;

  return {
    id: msg.id,
    senderId: isCurrentUserMessage
      ? 'currentUser'
      : isAssistantMessage
      ? 'assistant'
      : msg.userId,
    senderName: msg.userName || 'Unknown',
    content: msg.content,
    timestamp: new Date(msg.timestamp),
    tokenUsage: msg.tokenUsage ?? undefined,
    citations: citations ?? undefined,
    citationsUsed: citationsUsed ?? undefined,
  };
};

/**
 * Transforms multiple API messages to component message format.
 *
 * @param messages - Array of API messages to transform
 * @param currentUserId - The current user's ID for comparison
 * @returns Array of transformed AskMessage objects
 */
export const transformApiMessages = (
  messages: AskSessionMessage[],
  currentUserId: string
): AskMessage[] => {
  return messages.map((msg) => transformApiMessageToComponent(msg, currentUserId));
};

/**
 * Extracts file extension from a filename.
 *
 * @param fileName - The filename to extract extension from
 * @returns Uppercase file extension or 'FILE' as fallback
 */
export const getFileExtension = (fileName?: string): string => {
  return fileName?.split('.').pop()?.toUpperCase() || 'FILE';
};

/**
 * Detects if the user is on a Mac platform.
 * Used for displaying correct keyboard shortcut symbols.
 *
 * @returns True if user is on Mac, false otherwise
 */
export const detectMac = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  return (
    navigator.platform.includes('Mac') || navigator.userAgent.includes('Mac')
  );
};

/**
 * Calculates total token usage from a token usage object.
 *
 * @param tokenUsage - Token usage object with numeric values (may include undefined)
 * @returns Total number of tokens used
 */
export const calculateTotalTokens = (
  tokenUsage?: Record<string, number | undefined>
): number => {
  if (!tokenUsage) return 0;
  return Object.values(tokenUsage).reduce(
    (total: number, count) => total + (typeof count === 'number' ? count : 0),
    0
  );
};

/**
 * Formats a date to a localized string.
 *
 * @param date - Date object or timestamp to format
 * @returns Formatted date string
 */
export const formatDate = (date: Date | string | number): string => {
  const dateObj = typeof date === 'string' || typeof date === 'number' 
    ? new Date(date) 
    : date;
  return dateObj.toLocaleDateString();
};

/**
 * Formats a date and time to a string in the format: YYYY-MM-DD HH:MM:SS
 *
 * @param date - Date object or timestamp to format (defaults to current date)
 * @returns Formatted date and time string
 */
export const formatDateTime = (date: Date = new Date()): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

/**
 * Truncates text to a maximum length with ellipsis.
 *
 * @param text - Text to truncate
 * @param maxLength - Maximum length before truncation
 * @returns Truncated text with ellipsis if needed
 */
export const truncateText = (text: string, maxLength: number = 60): string => {
  if (text.length <= maxLength) {
    return text;
  }
  return `${text.substring(0, maxLength)}...`;
};

/**
 * Generates a consistent color for a user based on their ID.
 * Uses a hash function to ensure the same user always gets the same color.
 *
 * @param userId - User ID to generate color for
 * @param colorPalette - Array of color hex codes to choose from
 * @returns Hex color code
 */
export const generateUserColor = (
  userId: string,
  colorPalette: readonly string[] | string[] = [
    '#f59e0b', // amber
    '#ef4444', // red
    '#8b5cf6', // purple
    '#ec4899', // pink
    '#06b6d4', // cyan
    '#f97316', // orange
  ],
): string => {
  const index = userId
    .split('')
    .reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colorPalette[index % colorPalette.length];
};

/**
 * Races a promise against a timeout.
 *
 * Note: this does not abort the underlying work (e.g. a hanging `fetch`),
 * but it lets the UI recover so we don't get stuck in "Still processing...".
 */
/** True when a fetch or invoke was aborted via `AbortController`. */
export const isAbortError = (err: unknown): boolean =>
  err instanceof Error && err.name === 'AbortError';

export function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  label: string,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error(`${label} timed out after ${ms}ms`));
    }, ms);

    promise.then(
      (value) => {
        clearTimeout(timeoutId);
        resolve(value);
      },
      (err) => {
        clearTimeout(timeoutId);
        reject(err);
      },
    );
  });
}
